import { Col, Row } from 'reactstrap';
import { useContext, useEffect, useState } from 'react';
import { Trans, useLingui } from '@lingui/react';
import {
  ActionIcon,
  Button,
  Center,
  Group,
  SegmentedControl,
  Switch,
  Text,
  useMantineColorScheme,
} from '@mantine/core';
import { closeAllModals, openModal } from '@mantine/modals';
import { useNavigate } from 'react-router-dom';
import { TransButtonDefault } from '../../../Atoms/Buttons';
import { ModalDialog, useModalState } from '../../../Molecules/Dialogs';
import ChangePasswordDialog from '../../../Molecules/Settings/ChangePasswordDialog';
import ChangeEmailDialog from '../../../Molecules/Settings/ChangeEmailDialog';
import { AuthS } from '../../../Service/restapi/authService';
import { ToastS } from '../../../Service/ToastS';
import PanePaper from '../../../Molecules/PanePaper';
import { AuthContext, useJwtClaims } from '../../../Service/Context/AuthContext';
import { ApplicationUserContext } from '../../../Service/Context/ApplicationUserContext';
import { RoutingS } from '../../../Service/RoutingS';
import { ConstantS } from '../../../Service/ConstantS';
import { MixpanelS } from '../../../Service/MixpanelS';
import { useLocalStorage } from '@mantine/hooks';
import { IconBrowser, IconEdit, IconMoon, IconSun } from '@tabler/icons';
import ConfigOption from '../Google/ConfigOption';
import BookitupTextInput from '../../../Atoms/inputs/BookitupTextInput';
import { useBookitupSubscription } from '../../../Service/Context/BookitupSubscriptionContext';

const AccountSettings = () => {
  const { i18n } = useLingui();
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();
  const { show, toggle } = useModalState();
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const { logout } = useContext(AuthContext);
  const { applicationUser, savePatch } = useContext(ApplicationUserContext);
  const { firstName, lastName, redGreenWeakness } = applicationUser;
  const { loading, isActive } = useBookitupSubscription();

  const { email, role } = useJwtClaims();
  const isOwner = role === 'OWNER';

  const [colorScheme] = useLocalStorage({
    key: 'mantine-color-scheme',
    defaultValue: 'light',
    getInitialValueInEffect: true,
  });
  const { toggleColorScheme } = useMantineColorScheme();

  useEffect(() => {
    RoutingS.changeTitle('Zugangsdaten');
  }, []);

  function deleteAppUser() {
    AuthS.deleteAccount()
      .then((response) => {
        if (response.status === 200) {
          MixpanelS.track(ConstantS.TrackingEvents.AccountDeleted);
          logout();
        } else {
          ToastS.error('statusErrorTitle', i18n._(`errors.${response.status}.boxed.title`));
          ToastS.error('statusErrorDescription', i18n._(`errors.${response.status}.boxed.description`));
        }
      })
      .catch(() => {
        ToastS.error('statusError503', i18n._('errors.503.boxed.title'));
      });
  }

  function toggleEmailDialog() {
    setShowEmailDialog(!showEmailDialog);
  }

  const saveApplicationUserField = (field, value) => {
    if (applicationUser?.[field] === value) {
      return Promise.resolve(false);
    }

    return savePatch({ [field]: value });
  };

  const styles = {
    rowContainer: {
      display: 'flex',
      marginBottom: 20,
    },
    columnContainer: {
      flex: 1,
    },
    subject: {},
    deleteButton: {
      float: 'right',
    },
  };

  const openActiveSubscriptionModal = () =>
    openModal({
      title: (
        <Text weight="bolder" size="xl">
          Abonnement besteht
        </Text>
      ),
      children: (
        <>
          <Text my="md">
            Das Konto kann nicht gelöscht werden, da du derzeit ein aktives Abo hast. Bitte kündige erst das Abo.
          </Text>
          <Group position="right">
            <Button
              onClick={() => {
                closeAllModals();
                navigate('/settings/plans');
              }}
            >
              {i18n._('actions.edit')}
            </Button>
          </Group>
        </>
      ),
    });

  return (
    <div style={{ maxWidth: 650 }}>
      <PanePaper title="Zugangsdaten">
        <Group grow mb="xl">
          <BookitupTextInput
            key={`firstName-${firstName ?? ''}`}
            label={i18n._('applicationuser.firstName')}
            defaultValue={firstName}
            onBlurSave={(val) => saveApplicationUserField('firstName', val)}
          />
          <BookitupTextInput
            key={`lastName-${lastName ?? ''}`}
            label={i18n._('applicationuser.lastName')}
            defaultValue={lastName}
            onBlurSave={(val) => saveApplicationUserField('lastName', val)}
          />
        </Group>

        <Text mt="md" color="dimmed">
          E-Mail
        </Text>
        <Group mb="md">
          <Text size="md">{email}</Text>
          <ActionIcon size="xs" onClick={() => setShowEmailDialog(true)}>
            <IconEdit />
          </ActionIcon>
        </Group>

        <Text color="dimmed">Passwort</Text>
        <Group mb="xl">
          <Text size="md" mb={-8}>
            **************
          </Text>
          <ActionIcon size="xs" onClick={toggle}>
            <IconEdit />
          </ActionIcon>
        </Group>

        <div>
          <Text mb="sm" mt="md" color="dimmed">
            Farbschema
          </Text>
          <Center>
            <SegmentedControl
              size="xs"
              mb="md"
              color="blue"
              value={colorScheme}
              onChange={(val) => toggleColorScheme(val)}
              sx={{ width: 350 }}
              data={[
                {
                  value: 'auto',
                  label: (
                    <Group spacing="xs">
                      <IconBrowser />
                      <span>Auto</span>
                    </Group>
                  ),
                },
                {
                  value: 'dark',
                  label: (
                    <Group spacing="xs">
                      <IconMoon />
                      <span>Dunkel</span>
                    </Group>
                  ),
                },
                {
                  value: 'light',
                  label: (
                    <Group spacing="xs">
                      <IconSun />
                      <span>Hell</span>
                    </Group>
                  ),
                },
              ]}
            />
          </Center>

          <ConfigOption
            title="Farbenblinder Modus"
            rightSection={
              <Switch
                size="md"
                checked={redGreenWeakness}
                onLabel="AN"
                offLabel="AUS"
                onChange={() => savePatch({ redGreenWeakness: !redGreenWeakness })}
              />
            }
          />
          {isOwner && (
            <Center mt={50}>
              <Button
                color="red"
                variant="subtle"
                onClick={() => {
                  if (isActive) {
                    openActiveSubscriptionModal();
                  } else {
                    setModalOpen(true);
                  }
                }}
                disabled={loading}
              >
                {i18n._('settings.account.delete-account')}
              </Button>
            </Center>
          )}
        </div>
      </PanePaper>
      <Row>
        <Col sm={12}>
          <ModalDialog
            header={<Trans id="common.delete.appuser.confirm.head" />}
            body={<Trans id="common.delete.appuser.confirm" />}
            footer={
              <div>
                <TransButtonDefault
                  color="danger"
                  id="deleteAppUser"
                  onClickHandler={() => {
                    deleteAppUser();
                    setModalOpen(!modalOpen);
                  }}
                  text="actions.delete"
                />{' '}
                <TransButtonDefault onClickHandler={() => setModalOpen(!modalOpen)} text="actions.cancel" />
              </div>
            }
            show={modalOpen}
            toggle={() => setModalOpen(!modalOpen)}
          />

          <ChangePasswordDialog open={show} />
          <ChangeEmailDialog open={showEmailDialog} toggle={toggleEmailDialog} />
        </Col>
      </Row>
    </div>
  );
};

export default AccountSettings;
